import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';
import { useDebouncedCallback } from 'use-debounce';
import { AngleDownIcon, AngleUpIcon } from 'assets/icons';
import { minBulkChunkValue } from 'components/QuantityModule/QuantityModule';
import { REFERENCE_MAX_QUANTITY_LIMIT } from 'domains/basket/Basket.types';
import { Icon, WithTooltip } from 'UI';
import { ONCHANGE_DEBOUNCE_INTERVAL } from 'utils/hooks/useInput';
import { InputArrowsWrapper, InputWrapper, SInputNumber } from './QuantityBox.styled';

interface QuantityBoxProps {
  value: number;
  onChange: (quantity: number | string) => void;
  mini?: boolean;
  medium?: boolean;
  maxValue?: number;
  minValue?: number;
  onMaxValueOverflow?: () => void;
  chunk?: number;
  isMinBulkChunk?: boolean;
  isCompact?: boolean;
  status?: 'warning' | 'error';
  dataCy?: string;
  isSingleUnitRestricted?: boolean;
}

export const getSingleUnitIncrementDisabledTooltip = (
  t: (key: string, fallback: string) => string,
  isSingleUnitRestricted: boolean,
) =>
  isSingleUnitRestricted
    ? t(
        'cart.action.add_reference.traction_battery_single_unit_tooltip',
        'This part is restricted to single-unit orders',
      )
    : undefined;

const QuantityBoxLocal = ({
  value,
  onChange,
  mini = false,
  medium = false,
  maxValue,
  minValue,
  onMaxValueOverflow,
  chunk = 1,
  isMinBulkChunk = false,
  isCompact = false,
  status,
  dataCy,
  isSingleUnitRestricted = false,
}: QuantityBoxProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const [inputValue, setInputValue] = useState(value);
  const [inputKey, setInputKey] = useState(0);
  const debouncedOnChange = useDebouncedCallback(onChange, ONCHANGE_DEBOUNCE_INTERVAL);
  const maxValueLocal = maxValue ?? REFERENCE_MAX_QUANTITY_LIMIT;
  const minValueLocal = minValue ?? 0;
  const getChunk = () => (isMinBulkChunk ? minBulkChunkValue : chunk);
  const [isInteracting, setIsInteracting] = useState(false); // Track if the user is interacting
  const timeoutRef = useRef<NodeJS.Timeout | null>(null); // Reference to the timeout

  const clearUpdateTimeout = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  // Schedule the update after user interaction
  const scheduleUpdate = useCallback(() => {
    clearUpdateTimeout(); // Clear any previous timeout

    timeoutRef.current = setTimeout(() => {
      if (!isInteracting) {
        setInputValue(value);
      }
      setIsInteracting(false); // Reset interaction state
    }, 3000);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  useEffect(() => {
    if (!isInteracting) {
      setInputValue(value);
    } else {
      scheduleUpdate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isInteracting]);

  // Handle immediate updates from external value changes
  useEffect(() => {
    const isMaxValueOverflow = value > maxValueLocal;

    if (!isInteracting) {
      setInputValue(isMaxValueOverflow ? maxValueLocal : value);
    } else {
      scheduleUpdate();
    }

    isMaxValueOverflow && setInputValue(isMaxValueOverflow ? maxValueLocal : value);
    if (isMaxValueOverflow && onMaxValueOverflow) {
      onMaxValueOverflow();
    }
    return () => clearUpdateTimeout();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, maxValueLocal, onMaxValueOverflow]);

  const handleChange = useCallback(
    (val: number, noDebounceLocal: boolean) => {
      if (val < minValueLocal) return;
      setInputValue(val);
      noDebounceLocal ? onChange(val) : debouncedOnChange(val);
      setIsInteracting(true);
      scheduleUpdate();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [onChange, debouncedOnChange],
  );

  // Cleanup the timeout on component unmount
  useEffect(() => {
    return () => {
      clearUpdateTimeout(); // Clear the timeout when the component unmounts
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isIncrementBlocked = inputValue + getChunk() > maxValueLocal;
  const incrementDisabledTooltip = getSingleUnitIncrementDisabledTooltip(t, isSingleUnitRestricted);

  return (
    <InputWrapper data-cy={dataCy}>
      <SInputNumber
        status={status}
        key={`input-key-${inputKey}`}
        mini={mini}
        medium={medium}
        bordered
        digitsOnly
        value={inputValue}
        onBlur={(val) => {
          if (val === undefined || val === inputValue) return;
          const chunkModifier = isMinBulkChunk ? minBulkChunkValue : chunk;
          const newChunkValue = Math.floor(val / chunkModifier) * chunkModifier || chunkModifier;
          if (newChunkValue === inputValue) {
            setInputKey((prev) => prev + 1);
          }
          handleChange(newChunkValue, true);
        }}
        min={minValue}
        max={maxValueLocal}
        data-cy={'quantity-number'}
      />
      <InputArrowsWrapper>
        <WithTooltip title={isIncrementBlocked ? incrementDisabledTooltip : undefined}>
          <span data-cy={'quantity-up'}>
            <Icon
              IconComponent={AngleUpIcon}
              size={15}
              color={theme.color.grey55}
              hoverFill={theme.color.brand_black}
              display={'block'}
              onClick={() => {
                inputValue < maxValueLocal - getChunk() + 1 && handleChange(inputValue + getChunk(), false);
              }}
              disabled={isIncrementBlocked}
            />
          </span>
        </WithTooltip>
        <Icon
          IconComponent={AngleDownIcon}
          size={15}
          color={theme.color.grey55}
          hoverFill={theme.color.brand_black}
          display={'block'}
          onClick={() => {
            handleChange(inputValue - getChunk(), false);
          }}
          disabled={isCompact ? false : inputValue <= Math.max(minValueLocal, getChunk())}
          data-cy={'quantity-down'}
        />
      </InputArrowsWrapper>
    </InputWrapper>
  );
};

export const QuantityBox = React.memo(QuantityBoxLocal);
